import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS service（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import HelpHint from "../../components/help-hint/HelpHint";
import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import Input from "../../components/form-item/Input";
import AcrResultsTables from "../../components/acr/AcrResultsTables";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import {
  buildAcrTsv,
  countInProgressAcrRows,
  isAcrRowInProgress,
} from "../../utils/acr";
import type { AcrResultRow } from "../../types/acr";
import {
  AdminProjectSummary,
  adminAcrApi,
  useGetAdminAcrResultsQuery,
  useLazySearchAdminProjectsQuery,
  useBulkRescanProcessingMutation,
} from "../../app/services/adminAcr";

/** プロジェクト検索のデバウンス間隔（ms）。 */
const SEARCH_DEBOUNCE_MS = 250;
/** プロジェクト検索の取得件数。 */
const SEARCH_LIMIT = 30;
/** 照合中ファイルが残っている間の一覧ポーリング間隔（ms）。 */
const ACR_LIST_POLL_INTERVAL_MS = 30 * 1000;

// 照合結果の表を編集しやすいよう幅制限なしの全幅レイアウト
const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap24",
})`
  padding: 24px 16px 80px;
`;

const Section = styled.section.attrs({
  className: "u-col u-w100 u-gap12",
})``;

const Toolbar = styled.div.attrs({
  className: "u-row-between u-items-center u-w100 u-gap8",
})``;

const ToolbarActions = styled.div.attrs({
  className: "u-row u-items-center u-gap8",
})``;

const ProjectListWrapper = styled.div.attrs({
  className: "u-col u-w100",
})`
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  max-height: 240px;
  overflow-y: auto;
`;

const ProjectRow = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: ${({ $selected }) =>
    $selected ? "var(--soremo-bg-blue, rgba(0, 154, 206, 0.06))" : "transparent"};
  cursor: pointer;
  text-align: left;
  border-bottom: 1px solid var(--soremo-border);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--soremo-background, #fafafa);
  }
`;

const SelectedProjectCard = styled.div.attrs({
  className: "u-row u-items-center u-w100 u-gap12",
})`
  padding: 12px;
  border: 1px solid var(--soremo-blue);
  background: var(--soremo-bg-blue, rgba(0, 154, 206, 0.06));
  border-radius: 6px;
`;

/**
 * 管理者向け ACR Cloud 結果一覧ページ。
 *
 * プロジェクトを選択し、3 区分（ショット内メディア / ショットコメント添付 /
 * トークルーム添付）の ACR 結果を共有テーブル `AcrResultsTables` で表示する。
 * 右上に
 * - TSV クリップボードコピー（タブ区切り、3 区分を区分列付きで結合）
 * - 照合中（processing）の一括再検索（Celery 経由でリセット + 再投入）
 *
 * を備える。照合あり（matched）の行はクリックで詳細モーダルを開ける。
 * 権限は `role === "ADMIN"`（master_admin）のみ。
 */
const AdminAcrResults: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  // ===== プロジェクト検索 / 選択 =====
  const [searchInput, setSearchInput] = useState("");
  const debouncedQuery = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();

  const [selectedProject, setSelectedProject] =
    useState<AdminProjectSummary | null>(null);
  const [searchResults, setSearchResults] = useState<AdminProjectSummary[]>([]);

  const [searchProjects, { isFetching: isSearching }] =
    useLazySearchAdminProjectsQuery();

  // 検索クエリが変わったら再フェッチ
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await searchProjects({
          q: trimmedQuery,
          limit: SEARCH_LIMIT,
          offset: 0,
        }).unwrap();
        if (!cancelled) {
          setSearchResults(resp.data.projects);
        }
      } catch {
        if (!cancelled) {
          setSearchResults([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trimmedQuery, searchProjects]);

  // ===== ACR 結果取得 =====
  const projectId = selectedProject?.project_id ?? null;

  // 照合中ファイルが残っている間のみ一覧をポーリングし、完了を自動反映する。
  // ポーリング継続の判定は、購読を作らずキャッシュを読める useQueryState で
  // 行う（query の結果を自身のオプションに使う際の循環参照を避けるため）。
  const { data: cachedResults } =
    adminAcrApi.endpoints.getAdminAcrResults.useQueryState(projectId ?? "");
  const shouldPollResults = countInProgressAcrRows(cachedResults) > 0;

  const {
    data: acrResults,
    isFetching: isResultsFetching,
    isLoading: isResultsLoading,
    refetch: refetchResults,
  } = useGetAdminAcrResultsQuery(projectId ?? "", {
    skip: !projectId,
    pollingInterval: shouldPollResults ? ACR_LIST_POLL_INTERVAL_MS : 0,
  });

  // `?? []` で毎レンダー新しい空配列を作ると React Hook の依存比較が壊れる
  // ため、`useMemo` で参照を安定させる（exhaustive-deps 警告対策）。
  const shotMedia = useMemo<AcrResultRow[]>(
    () => acrResults?.data.shot_media ?? [],
    [acrResults],
  );
  const shotCommentMedia = useMemo<AcrResultRow[]>(
    () => acrResults?.data.shot_comment_media ?? [],
    [acrResults],
  );
  const talkRoomMedia = useMemo<AcrResultRow[]>(
    () => acrResults?.data.talk_room_media ?? [],
    [acrResults],
  );

  // pending も「楽曲照合中」表示なので件数・一括再スキャン対象に含める
  const processingCount = useMemo(
    () =>
      shotMedia.filter(isAcrRowInProgress).length +
      shotCommentMedia.filter(isAcrRowInProgress).length +
      talkRoomMedia.filter(isAcrRowInProgress).length,
    [shotMedia, shotCommentMedia, talkRoomMedia],
  );

  // ===== クリップボードコピー（タブ区切り。Excel / Google Sheets でセル分割される） =====
  const { copy } = useCopyToClipboard({
    successMessage: "表をコピーしました",
    errorMessage: "コピーに失敗しました",
  });

  const handleCopyTable = useCallback(() => {
    if (!acrResults) return;
    const tsv = buildAcrTsv(shotMedia, shotCommentMedia, talkRoomMedia);
    void copy(tsv);
  }, [acrResults, shotMedia, shotCommentMedia, talkRoomMedia, copy]);

  // ===== 一括再スキャン =====
  const [bulkRescan, { isLoading: isRescanning }] =
    useBulkRescanProcessingMutation();

  const handleBulkRescan = useCallback(async () => {
    if (!projectId || isRescanning) return;
    try {
      const resp = await bulkRescan(projectId).unwrap();
      const count = resp.data.rescan_count;
      if (count > 0) {
        toast.success(`照合中 ${count} 件の再スキャンをキューに投入しました`);
      } else {
        toast.success("照合中のファイルはありませんでした");
      }
      // 再投入直後は acr_status='1'(pending) に戻っているので一覧を最新化。
      void refetchResults();
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "再スキャンの起動に失敗しました";
      toast.error(message);
    }
  }, [projectId, isRescanning, bulkRescan, refetchResults]);

  // ===== 権限ガード =====
  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }
  const role: string | undefined = currentUser?.data?.role;
  if (role !== "ADMIN") {
    return <Navigate to="/projects" replace />;
  }

  return (
    <PageWrapper>
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="ACR Cloud 結果一覧" />
        <HelpHint description={"プロジェクトを選択し、ショット内メディア・ショットコメント添付・トークルーム添付の ACR Cloud スキャン結果を一覧表示します。照合ありの行はクリックで詳細を確認できます。TSV のクリップボードコピーと、照合中ファイルの一括再スキャンが可能です。"} />
      </div>

      <Section>
        <h2 className="heading-13">1. プロジェクトを選択</h2>
        <Input
          type="search"
          placeholder="プロジェクト名・コード名で絞り込み"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {selectedProject && (
          <SelectedProjectCard>
            <div className="u-col u-fill u-gap2">
              <span className="heading-13">{selectedProject.name}</span>
              <span className="bodytext-11 u-text-light-gray">
                ID: {selectedProject.project_id}
                {selectedProject.code_name
                  ? ` / ${selectedProject.code_name}`
                  : ""}
              </span>
            </div>
            <Button
              size="small"
              theme="tertiary"
              variant="text"
              onClick={() => setSelectedProject(null)}
            >
              選択を解除
            </Button>
          </SelectedProjectCard>
        )}
        {!selectedProject && (
          <ProjectListWrapper>
            {isSearching && searchResults.length === 0 ? (
              <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                読み込み中...
              </p>
            ) : searchResults.length === 0 ? (
              <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                該当するプロジェクトがありません。
              </p>
            ) : (
              searchResults.map((project) => (
                <ProjectRow
                  key={project.project_id}
                  type="button"
                  onClick={() => setSelectedProject(project)}
                >
                  <span className="bodytext-13">{project.name}</span>
                  <span className="bodytext-11 u-text-light-gray">
                    {project.code_name}
                  </span>
                </ProjectRow>
              ))
            )}
          </ProjectListWrapper>
        )}
      </Section>

      {selectedProject && (
        <>
          {/* ===== ツールバー（右上アクション） ===== */}
          <Toolbar>
            <span className="bodytext-13 u-text-light-gray">
              {acrResults
                ? `全${shotMedia.length + shotCommentMedia.length + talkRoomMedia.length}件 / 照合中 ${processingCount}件`
                : "読み込み中..."}
            </span>
            <ToolbarActions>
              <Button
                size="small"
                theme="primary"
                variant="outlined"
                leftIcon="refresh"
                onClick={() => void handleBulkRescan()}
                disabled={
                  isRescanning || !acrResults || processingCount === 0
                }
              >
                {isRescanning ? "再スキャン中..." : "照合中を一括再検索"}
              </Button>
              <IconButton
                icon="content_copy"
                size={20}
                title="表をコピー（Excel / スプレッドシートに貼り付け可能）"
                ariaLabel="表をコピー"
                onClick={handleCopyTable}
                disabled={!acrResults}
              />
            </ToolbarActions>
          </Toolbar>

          {/* ===== 3 区分のテーブル ===== */}
          {isResultsLoading ? (
            <SvgLoadingAnimation />
          ) : (
            <AcrResultsTables
              shotMedia={shotMedia}
              shotCommentMedia={shotCommentMedia}
              talkRoomMedia={talkRoomMedia}
              isFetching={isResultsFetching}
              enableRescan
              onRescanSuccess={() => void refetchResults()}
            />
          )}
        </>
      )}
    </PageWrapper>
  );
};

export default AdminAcrResults;
